import {patchState, signalStore, withComputed, withMethods, withProps, withState} from '@ngrx/signals';
import {removeAllEntities, setAllEntities, withEntities} from '@ngrx/signals/entities';
import {Device} from 'src/app/infrastructure/moovement-api/models/asset';
import {computed, inject} from '@angular/core';
import {DeviceAssociationStore} from 'src/app/domain/devices/device-association.store';
import {
  isGpsProximityAccelerometerTag,
  isGpsTag,
  isNfcTag,
  isProximityTag,
  isTag
} from 'src/app/domain/devices/tag/tag';
import {isBluetoothProximitySensor} from 'src/app/domain/devices/bluetooth-proximity-sensor/bluetooth-proximity-sensor';
import {DeviceType} from 'src/app/domain/devices/device';
import {isWaterMonitor} from 'src/app/domain/devices/water-monitor/water-monitor';

const initialState = {
  loadingDevices: true
};

export const DeviceStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withEntities<Device>(),
  withProps(() => ({
    deviceAssociationStore: inject(DeviceAssociationStore)
  })),
  withMethods((store) => ({
    reset() {
      patchState(store, removeAllEntities(), initialState);
    },
    setAllEntities(devices: Device[]) {
      patchState(store, setAllEntities(devices), { loadingDevices: false });
    }
  })),
  withComputed(({ entities, deviceAssociationStore, loadingDevices }) => ({
    assignedDevices: computed(() => {
      const associatedDeviceIds = deviceAssociationStore.associatedDeviceIds();
      return entities().filter(device => associatedDeviceIds.has(device.id));
    }),
    loading: computed(() => {
      const loadingDeviceAssociations = deviceAssociationStore.loading();
      return loadingDevices() || loadingDeviceAssociations;
    }),
    unassignedDevices: computed(() => {
      const associatedDeviceIds = deviceAssociationStore.associatedDeviceIds();
      return entities().filter(device => !associatedDeviceIds.has(device.id));
    })
  })),
  withComputed(({ assignedDevices, entities, unassignedDevices }) => ({
    proximityDevices: computed(() => {
      return assignedDevices().filter(isBluetoothProximitySensor);
    }),
    precipitationDevices: computed(() => {
      return assignedDevices().filter(device => device.capabilities.includes('Precipitation'));
    }),
    assignedTags: computed(() => {
      return assignedDevices().filter(device => isTag(device));
    }),
    unassignedTags: computed(() => {
      return unassignedDevices().filter(device => isTag(device));
    }),
    unassignedBluetoothProximitySensors: computed(() => {
      return unassignedDevices().filter(isBluetoothProximitySensor);
    }),
    unassignedWaterMonitors: computed(() => {
      return unassignedDevices().filter(isWaterMonitor);
    }),
    deviceIdToType: computed(() => {
      const result = new Map<string, DeviceType>();
      for (const device of entities()) {
        const deviceId = device.id;
        if (isBluetoothProximitySensor(device)) {
          result.set(deviceId, DeviceType.BLUETOOTH_PROXIMITY_SENSOR);
        } else if (isGpsProximityAccelerometerTag(device)) {
          result.set(deviceId, DeviceType.GPS_PROXIMITY_ACCELEROMETER_TAG);
        } else if (isGpsTag(device)) {
          result.set(deviceId, DeviceType.GPS_TAG);
        } else if (isNfcTag(device)) {
          result.set(deviceId, DeviceType.NFC_TAG);
        } else if (isProximityTag(device)) {
          result.set(deviceId, DeviceType.PROXIMITY_TAG);
        }
      }
      return result;
    })
  }))
);
